/**
 * The engine voice as numbers (pure, unit tested): what the synthesiser sets for an engine speed,
 * throttle and engine type. The sound is built from the firing frequency (cylinders x rpm), a
 * half-order rumble, a load-dependent low-pass (an open throttle is brighter and louder), a little
 * combustion noise and soft saturation. Every value is smooth in rpm and throttle, so the audio
 * graph only glides (no clicks).
 */

export interface EngineType {
  cylinders: number;
  /** Strength of the half-order (lumpy V8, diesel knock) and of the combustion noise. */
  rumble: number;
  grit: number;
  /** Brightness of the low-pass and the saturation drive. */
  bright: number;
  drive: number;
}

export const ENGINES: Record<string, EngineType> = {
  city: { cylinders: 4, rumble: 0.25, grit: 0.3, bright: 0.9, drive: 1.4 },
  sedan: { cylinders: 4, rumble: 0.3, grit: 0.25, bright: 0.85, drive: 1.5 },
  taxi: { cylinders: 6, rumble: 0.45, grit: 0.25, bright: 0.75, drive: 1.6 },
  police: { cylinders: 8, rumble: 0.7, grit: 0.25, bright: 0.8, drive: 2.0 },
  suv: { cylinders: 6, rumble: 0.5, grit: 0.3, bright: 0.7, drive: 1.6 },
  sport: { cylinders: 8, rumble: 0.8, grit: 0.2, bright: 1.15, drive: 2.4 },
  van: { cylinders: 4, rumble: 0.35, grit: 0.4, bright: 0.65, drive: 1.3 },
  bus: { cylinders: 6, rumble: 0.9, grit: 0.7, bright: 0.45, drive: 1.5 },
  bike: { cylinders: 1, rumble: 0, grit: 0, bright: 0.5, drive: 1 },
  scooter: { cylinders: 1, rumble: 0, grit: 0, bright: 0.5, drive: 1 },
};

export interface EngineVoice {
  /** Firing frequency (Hz) and the half-order's. */
  fire: number;
  half: number;
  /** Low-pass cutoff (Hz) and its resonance. */
  cutoff: number;
  q: number;
  /** Gains (0..1): the tone, the rumble, the combustion noise, the whole voice. */
  tone: number;
  rumble: number;
  noise: number;
  level: number;
  /** Centre of the noise band (Hz). */
  noiseHz: number;
}

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/**
 * The voice at `rpm` (between idle and the redline), `throttle` 0..1. Electric bikes and scooters
 * get a quiet motor whine instead (one "cylinder" = the motor's pole frequency).
 */
export function engineVoice(
  type: EngineType,
  rpm: number,
  throttle: number,
  idle: number,
  redline: number,
): EngineVoice {
  const r = clamp((rpm - idle) / Math.max(1, redline - idle), 0, 1.2);
  const th = clamp(throttle, 0, 1);
  if (type.cylinders <= 1) {
    const f = 180 + rpm * 0.35;
    return {
      fire: f,
      half: f / 2,
      cutoff: 1800 + r * 2500,
      q: 0.7,
      tone: 0.25 + th * 0.2,
      rumble: 0,
      noise: 0.05,
      level: 0.12 + r * 0.12 + th * 0.06,
      noiseHz: 3000,
    };
  }
  const fire = (Math.max(rpm, 300) / 60) * (type.cylinders / 2);
  const cutoff = (320 + fire * 3.2 + th * 2600 * type.bright + r * 1400) * type.bright;
  return {
    fire,
    half: fire / 2,
    cutoff: clamp(cutoff, 180, 9000),
    q: 0.9 + th * 1.6,
    tone: 0.55 + th * 0.35,
    rumble: type.rumble * (0.7 - r * 0.3),
    noise: type.grit * (0.08 + th * 0.22),
    level: 0.22 + r * 0.38 + th * 0.28,
    noiseHz: clamp(fire * 2.2, 120, 4000),
  };
}

/** Harmonic amplitudes of the engine's periodic wave (index 1 = fundamental). */
export function engineHarmonics(type: EngineType, n = 24): Float32Array {
  const a = new Float32Array(n + 1);
  for (let k = 1; k <= n; k++) {
    // A falling spectrum with the even orders a touch stronger (exhaust pulses), deterministic.
    const tilt = 1 / Math.pow(k, 1.1 - type.bright * 0.25);
    const even = k % 2 === 0 ? 1.25 : 1;
    const wobble = 0.75 + 0.25 * Math.cos(k * 1.7 + type.cylinders);
    a[k] = tilt * even * wobble;
  }
  return a;
}
